<template>
    <v-card flat class="my-0">
        <v-card-text>
            <form @submit.prevent novalidate>
            <v-text-field v-model="field.mask" v-bind:label="$t('Mask')"></v-text-field>
            <NumericInputIso
                :fieldValue="field.default"
                :fieldTextTranslation="$t('DefaultValue')"
                :scale="parseInt(field.scale)"
                :userThouSep="userThouSep"
                :userDecSep="userDecSep"
                :maxValue="parseFloat(field.maxVal)"
                :minValue="parseFloat(field.minVal)"
                @onValueChanged="handleValueChange"
            />
            <NumericInputIso
                :fieldValue="field.minVal"
                :fieldTextTranslation="$t('MinValue')"
                :scale="0"
                :minValue="NUMERIC_MIN_VALUE"
                :maxValue="field.maxVal"
                :userThouSep="userThouSep"
                :userDecSep="userDecSep"
                @onValueChanged="handleMinValueChange"
            />
            <NumericInputIso
                :fieldValue="field.maxVal"
                :fieldTextTranslation="$t('MaxValue')"
                :scale="0"
                :userThouSep="userThouSep"
                :userDecSep="userDecSep"
                @onValueChanged="validateMaxValueAndApply"
                :maxValue="NUMERIC_MAX_VALUE"
                :minValue="field.minVal"
                :errorMessages="maxLengthErrors"
            />
            <v-text-field v-model="field.scale" type="number" v-bind:label="$t('Decimals')"
                :error-messages="scaleErrors" @change="applyDefaultValueRestrictions" min="0" :max="NUMERIC_MAX_SCALE">
            </v-text-field>
            </form>
        </v-card-text>
    </v-card>
</template>

<script>
import { maxValue, minValue } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Utils from '@/ecm/common/Utils';
import { IMaskComponent } from 'vue-imask';
import { NUMERIC_MAX_SCALE, NUMERIC_MAX_VALUE, NUMERIC_MIN_VALUE } from '@/ecm/common/Constants';
import NumericInputIso from './inputs/NumericInputISO.vue';

export default {
    props: {
        field: {
            type: Object,
            default: () => ({
                default: '',
                scale: 0
            }),
        },
        validator: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        'imask-input': IMaskComponent,
        NumericInputIso
    },
    mixins: [validationMixin],
    data() {
        return {
            displayValue: '',
            NUMERIC_MAX_VALUE: NUMERIC_MAX_VALUE,
            NUMERIC_MAX_SCALE: NUMERIC_MAX_SCALE,
            NUMERIC_MIN_VALUE: NUMERIC_MIN_VALUE
        };
    },
    validations: {
        field: {
            scale: {
                maxValue: maxValue(NUMERIC_MAX_SCALE),
                minValue: minValue(0),
            },
            maxVal: {
                maxValue: Utils.validators.numericFieldMaxValue,
                minValue: minValue(NUMERIC_MIN_VALUE)
            },
            minVal: {
                maxValue: Utils.validators.numericFieldMaxValue,
                minValue: minValue(NUMERIC_MIN_VALUE)
            }
        },
    },
    computed: {
        userDecSep() {
            return this.$store.getters['account/user'].MetricDecimalSep;
        },
        userThouSep() {
            return this.$store.getters['account/user'].MetricThousandSep;
        },
        scaleErrors() {
            const errors = [];
            if (this.field.scale > NUMERIC_MAX_SCALE || this.field.scale < 0) {
                errors.push(this.$t('MaxMinValueDecimal'));
            }
            return errors;
        },
        maxLengthErrors() {
            const errors = [];
            if (!this.$v.field.maxVal.$dirty) return errors;

            if (!this.$v.field.maxVal.maxValue) {
                errors.push(`${this.$t('MaxValue')}: ${NUMERIC_MAX_VALUE}`);
            }
            return errors;
        }
    },
    methods: {
        handleValueChange(newValue) {
            this.field.default = String(newValue);
        },
        handleMinValueChange(newValue) {
            this.field.minVal = String(newValue);
            this.applyDefaultValueRestrictions();
        },
        applyDefaultValueRestrictions() {
            if (!this.field.default || this.field.default === '') return;

            let value = this.field.default.toString();
            let number = parseFloat(value);

            if (isNaN(number)) return;

            const maxVal = parseFloat(this.field.maxVal);
            const minVal = parseFloat(this.field.minVal);

            if (!isNaN(maxVal) && number > maxVal) {
                number = maxVal;
                value = maxVal.toString();
            }

            if (!isNaN(minVal) && number < minVal) {
                number = minVal;
                value = minVal.toString();
            }

            const scale = parseInt(this.field.scale || 0);

            const parts = value.split('.');
            const integerPart = parts[0];
            let decimalPart = parts.length > 1 ? parts[1] : '';

            if (decimalPart.length > scale) {
                decimalPart = decimalPart.substring(0, scale);
            } else if (decimalPart.length < scale) {
                decimalPart = decimalPart.padEnd(scale, '0');
            }

            const formattedValue = scale > 0
                ? `${integerPart}.${decimalPart}`
                : integerPart;

            this.field.default = formattedValue;
        },

        validateMaxValueAndApply(newValue) {
            this.field.maxVal = String(newValue);
            this.$v.field.maxVal.$touch();
            this.applyDefaultValueRestrictions();
        },
    },
    created() {
        this.field.isNewValueFormat = true;
    }
};
</script>